import backendCall from "./AxiosService";

/**
 * Helper to extract data from wrapped API responses.
 * Backend returns { success: true, data: [...] } but we need just the data array.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractData = <T>(responseData: any): T => {
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return responseData.data;
  }
  return responseData;
};

// Inventory Category
export interface InventoryCategory {
  id: number;
  name: string;
  description: string | null;
  created_at: Date;
  updated_at: Date;
}

// Inventory Supplier
export interface InventorySupplier {
  id: number;
  name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  created_at: Date;
  updated_at: Date;
}

// Inventory Item
export interface InventoryItem {
  id: number;
  name: string;
  description: string | null;
  category_id: number | null;
  sku: string | null;
  part_number: string | null;
  unit_of_measure: string;
  minimum_quantity: number;
  current_quantity: number;
  reorder_point: number;
  reorder_quantity: number;
  location: string | null;
  location_id: number | null;
  supplier_id: number | null;
  cost_per_unit: number | null;
  notes: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface InventoryItemWithDetails extends InventoryItem {
  category?: InventoryCategory;
  supplier?: InventorySupplier;
  locations?: InventoryItemLocation[];
  total_quantity?: number;
}

// Inventory Item Location
export interface InventoryItemLocation {
  id: number;
  item_id: number;
  location_id: number;
  quantity: number;
  created_at: Date;
  updated_at: Date;
}

export interface InventoryItemLocationWithDetails extends InventoryItemLocation {
  item?: InventoryItem;
  location?: InventoryLocation;
}

// Inventory Transaction
export type TransactionType = 'receive' | 'issue' | 'adjust' | 'return';

export interface InventoryTransaction {
  id: number;
  item_id: number;
  transaction_type: TransactionType;
  quantity: number;
  previous_quantity: number;
  new_quantity: number;
  transaction_date: Date;
  user_id: number | null;
  check_id: number | null;
  notes: string | null;
  created_at: Date;
}

export interface InventoryTransactionWithDetails extends InventoryTransaction {
  item?: InventoryItem;
  user_name?: string;
  request_info?: {
    id: number;
    equipment_id: string;
    request_type: string;
  };
}


// API Functions for Categories
export async function getAllCategories(): Promise<InventoryCategory[]> {
  const response = await backendCall.get('inventory/categories');
  return extractData(response.data);
}

export async function getCategoryById(id: number): Promise<InventoryCategory> {
  const response = await backendCall.get(`inventory/categories/${id}`);
  return response.data;
}

export async function createCategory(category: Pick<InventoryCategory, 'name' | 'description'>): Promise<InventoryCategory> {
  const response = await backendCall.post('inventory/categories', category);
  return response.data;
}

export async function updateCategory(id: number, category: Pick<InventoryCategory, 'name' | 'description'>): Promise<InventoryCategory> {
  const response = await backendCall.put(`inventory/categories/${id}`, category);
  return response.data;
}

export async function deleteCategory(id: number): Promise<void> {
  await backendCall.delete(`inventory/categories/${id}`);
}

// API Functions for Suppliers
export async function getAllSuppliers(): Promise<InventorySupplier[]> {
  const response = await backendCall.get('inventory/suppliers');
  return extractData(response.data);
}

export async function getSupplierById(id: number): Promise<InventorySupplier> {
  const response = await backendCall.get(`inventory/suppliers/${id}`);
  return response.data;
}

export async function createSupplier(supplier: Partial<InventorySupplier>): Promise<InventorySupplier> {
  const response = await backendCall.post('inventory/suppliers', supplier);
  return response.data;
}

export async function updateSupplier(id: number, supplier: Partial<InventorySupplier>): Promise<InventorySupplier> {
  const response = await backendCall.put(`inventory/suppliers/${id}`, supplier);
  return response.data;
}

export async function deleteSupplier(id: number): Promise<void> {
  await backendCall.delete(`inventory/suppliers/${id}`);
}

// API Functions for Items
export async function getAllItems(withDetails: boolean = false): Promise<InventoryItem[] | InventoryItemWithDetails[]> {
  const response = await backendCall.get('inventory/items', {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

// Search inventory items by query
export async function searchItems(query: string, withDetails: boolean = true): Promise<InventoryItem[] | InventoryItemWithDetails[]> {
  const response = await backendCall.get('inventory/items', {
    params: {
      search: query,
      details: withDetails
    }
  });
  return extractData(response.data);
}

// Search inventory items by query and filter by location
export async function searchItemsByLocation(query: string, locationId: number | null = null, withDetails: boolean = true): Promise<InventoryItem[] | InventoryItemWithDetails[]> {
  let items = await searchItems(query, withDetails);
  
  // If a location is specified, filter items available at that location and update quantities
  if (locationId) {
    try {
      const itemsAtLocation = await getItemLocationsByLocationId(locationId, true) as InventoryItemLocationWithDetails[];
      const locationQuantities = new Map(itemsAtLocation.map(il => [il.item_id, il.quantity]));
      
      // Filter items that are available at the location and update their quantities
      items = items.filter(item => locationQuantities.has(item.id))
        .map(item => ({
          ...item,
          current_quantity: locationQuantities.get(item.id) || 0
        }));
    } catch (error) {
      console.error('Error filtering items by location:', error);
    }
  }
  
  return items;
}

export async function getItemById(id: number, withDetails: boolean = false): Promise<InventoryItem | InventoryItemWithDetails> {
  const response = await backendCall.get(`inventory/items/${id}`, {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

export async function getItemsToReorder(): Promise<InventoryItem[]> {
  const response = await backendCall.get('inventory/items/reorder');
  return extractData(response.data);
}

export async function createItem(item: Partial<InventoryItem>): Promise<InventoryItem> {
  const response = await backendCall.post('inventory/items', item);
  return response.data;
}

export async function updateItem(id: number, item: Partial<InventoryItem>): Promise<InventoryItem> {
  const response = await backendCall.put(`inventory/items/${id}`, item);
  return response.data;
}

export async function updateItemQuantity(id: number, quantity: number): Promise<InventoryItem> {
  const response = await backendCall.patch(`inventory/items/${id}/quantity`, { quantity });
  return response.data;
}

export async function deleteItem(id: number): Promise<void> {
  await backendCall.delete(`inventory/items/${id}`);
}

// API Functions for Transactions
export async function getAllTransactions(withDetails: boolean = false): Promise<InventoryTransaction[] | InventoryTransactionWithDetails[]> {
  const response = await backendCall.get('inventory/transactions', {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

export async function getTransactionById(id: number, withDetails: boolean = true): Promise<InventoryTransaction | InventoryTransactionWithDetails> {
  const response = await backendCall.get(`inventory/transactions/${id}`, {
    params: { details: withDetails }
  });
  return response.data;
}

export async function getTransactionsByItemId(itemId: number, withDetails: boolean = false): Promise<InventoryTransaction[] | InventoryTransactionWithDetails[]> {
  const response = await backendCall.get(`inventory/items/${itemId}/transactions`, {
    params: { details: withDetails }
  });
  return response.data;
}

export async function createTransaction(transaction: Omit<InventoryTransaction, 'id' | 'created_at'>): Promise<InventoryTransaction> {
  const response = await backendCall.post('inventory/transactions', transaction);
  return response.data;
}

export async function deleteTransaction(id: number): Promise<void> {
  await backendCall.delete(`inventory/transactions/${id}`);
}


// Inventory Location Interfaces and Functions
export interface InventoryLocation {
  id: number;
  name: string;
  description: string | null;
  is_truck: boolean;
  truck_id: number | null;
  created_at: Date;
  updated_at: Date;
}

export async function getAllLocations(): Promise<InventoryLocation[]> {
  const response = await backendCall.get('inventory/locations');
  // Ensure we ALWAYS return an array, even if the API returns null/undefined
  const data = response.data.data || response.data;
  return Array.isArray(data) ? data : [];
}

export async function getTruckLocations(): Promise<InventoryLocation[]> {
  const response = await backendCall.get('inventory/locations/trucks');
  return response.data;
}

export async function getNonTruckLocations(): Promise<InventoryLocation[]> {
  const response = await backendCall.get('inventory/locations/non-trucks');
  return response.data;
}

export async function getLocationById(id: number): Promise<InventoryLocation> {
  const response = await backendCall.get(`inventory/locations/${id}`);
  return response.data;
}

export async function createLocation(location: Partial<InventoryLocation>): Promise<InventoryLocation> {
  const response = await backendCall.post('inventory/locations', location);
  return response.data;
}

export async function updateLocation(id: number, location: Partial<InventoryLocation>): Promise<InventoryLocation> {
  const response = await backendCall.put(`inventory/locations/${id}`, location);
  return response.data;
}

export async function deleteLocation(id: number): Promise<void> {
  await backendCall.delete(`inventory/locations/${id}`);
}

export async function syncTrucksAsLocations(): Promise<{ created: number, updated: number }> {
  const response = await backendCall.post('inventory/locations/sync-trucks');
  return {
    created: response.data.created,
    updated: response.data.updated
  };
}

// Inventory Transfer Interfaces and Functions
export interface InventoryTransfer {
  id: number;
  inventory_item_id: number;
  from_location_id: number | null;
  to_location_id: number;
  quantity_transferred: number;
  user_id: number | null;
  notes: string | null;
  transfer_date: Date;
}

export interface InventoryTransferWithDetails extends InventoryTransfer {
  item?: InventoryItem;
  source_location?: InventoryLocation | null;
  destination_location?: InventoryLocation;
  user_name?: string | null;
}

export async function getAllTransfers(withDetails: boolean = false, limit?: number): Promise<InventoryTransfer[] | InventoryTransferWithDetails[]> {
  const response = await backendCall.get('inventory/transfers', {
    params: {
      details: withDetails,
      limit: limit
    }
  });
  return extractData(response.data);
}

export async function getTransferById(id: number): Promise<InventoryTransfer> {
  const response = await backendCall.get(`inventory/transfers/${id}`);
  return response.data;
}

export async function getTransfersByItemId(itemId: number): Promise<InventoryTransfer[]> {
  const response = await backendCall.get(`inventory/items/${itemId}/transfers`);
  return response.data;
}

export async function getTransfersBySourceLocationId(locationId: number): Promise<InventoryTransfer[]> {
  const response = await backendCall.get(`inventory/locations/${locationId}/source-transfers`);
  return response.data;
}

export async function getTransfersByDestinationLocationId(locationId: number): Promise<InventoryTransfer[]> {
  const response = await backendCall.get(`inventory/locations/${locationId}/destination-transfers`);
  return response.data;
}

export async function createTransfer(transfer: Partial<InventoryTransfer>): Promise<InventoryTransfer> {
  const response = await backendCall.post('inventory/transfers', transfer);
  return response.data;
}

export async function deleteTransfer(id: number): Promise<void> {
  await backendCall.delete(`inventory/transfers/${id}`);
}

// Inventory Item Locations Functions
export async function getAllItemLocations(withDetails: boolean = false): Promise<InventoryItemLocation[] | InventoryItemLocationWithDetails[]> {
  const response = await backendCall.get('inventory/item-locations', {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

export async function getItemLocationById(id: number): Promise<InventoryItemLocation> {
  const response = await backendCall.get(`inventory/item-locations/${id}`);
  return response.data;
}

export async function getItemLocationsByItemId(itemId: number, withDetails: boolean = false): Promise<InventoryItemLocation[] | InventoryItemLocationWithDetails[]> {
  const response = await backendCall.get(`inventory/items/${itemId}/locations`, {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

export async function getItemLocationsByLocationId(locationId: number, withDetails: boolean = false): Promise<InventoryItemLocation[] | InventoryItemLocationWithDetails[]> {
  const response = await backendCall.get(`inventory/locations/${locationId}/items`, {
    params: { details: withDetails }
  });
  return extractData(response.data);
}

export async function getItemLocationByItemAndLocationId(itemId: number, locationId: number): Promise<InventoryItemLocation | null> {
  try {
    const response = await backendCall.get(`inventory/items/${itemId}/locations/${locationId}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getTotalQuantityByItemId(itemId: number): Promise<number> {
  const response = await backendCall.get(`inventory/items/${itemId}/total-quantity`);
  return response.data.total_quantity;
}

export async function getAllItemsTotalQuantities(): Promise<Record<number, number>> {
  const response = await backendCall.get('inventory/items-total-quantities');
  return response.data;
}

export async function createItemLocation(itemLocation: Partial<InventoryItemLocation>): Promise<InventoryItemLocation> {
  const response = await backendCall.post('inventory/item-locations', itemLocation);
  return response.data;
}

export async function updateItemLocationQuantity(itemId: number, locationId: number, quantity: number): Promise<InventoryItemLocation> {
  const response = await backendCall.patch(`inventory/items/${itemId}/locations/${locationId}/quantity`, { quantity });
  return response.data;
}

export async function deleteItemLocation(id: number): Promise<void> {
  await backendCall.delete(`inventory/item-locations/${id}`);
}

export async function deleteItemLocationsByItemId(itemId: number): Promise<void> {
  await backendCall.delete(`inventory/items/${itemId}/locations`);
}

export async function deleteItemLocationsByLocationId(locationId: number): Promise<void> {
  await backendCall.delete(`inventory/locations/${locationId}/items`);
}

// Get the available quantity of a specific item at a specific location
export async function getItemQuantityAtLocation(itemId: number, locationId: number): Promise<number> {
  try {
    const itemLocation = await getItemLocationByItemAndLocationId(itemId, locationId);
    return itemLocation?.quantity || 0;
  } catch (error) {
    console.error('Error getting item quantity at location:', error);
    return 0;
  }
}

// Service object for easy default import
const inventoryService = {
  // Search function for inventory items
  searchInventoryItems: async (query: string) => {
    try {
      const response = await backendCall.get('inventory/items', {
        params: {
          search: query,
          details: true
        }
      });
      return {
        success: true,
        data: extractData(response.data)
      };
    } catch (error) {
      console.error('Error searching inventory items:', error);
      return {
        success: false,
        data: [],
        error
      };
    }
  },
  
  // Export other commonly used functions
  getAllItems,
  searchItems,
  searchItemsByLocation,
  getItemById,
  createItem,
  updateItem,
  getAllCategories,
  getAllSuppliers,
  getAllLocations,
  getTruckLocations,
  getNonTruckLocations,
  getItemLocationsByItemId,
  getAllTransactions
};

export default inventoryService;
